import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';
import interviewService from '../services/interviewService';
import ScheduleInterviewModal from '../components/ScheduleInterviewModal';
import { getEventTypeClass, getEventTypeColor, getEventTypeIcon, getEventTypeBadge, EVENT_TYPE_LABELS } from '../constants/eventTypes';

const InterviewCalendarPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  useEffect(() => {
    fetchCalendarData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();

      const startDate = new Date(year, month, 1, 0, 0, 0, 0);
      const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);

      const response = await interviewService.getCalendar(
        startDate.toISOString(),
        endDate.toISOString()
      );

      if (response.success) {
        setCalendarData(response.data);
      }
    } catch (err) {
      console.error('Failed to load calendar:', err);
    } finally {
      setLoading(false);
    }
  };

  const getLocalDateKey = (value) => {
    const dt = new Date(value);
    const year = dt.getFullYear();
    const month = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const days = [];
    const startPadding = firstDay.getDay();

    // Add padding days from previous month
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }

    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const getInterviewsForDate = (date) => {
    if (!date) return [];
    const selectedDateKey = getLocalDateKey(date);

    const matchedEvents = [];
    calendarData.forEach((dayData) => {
      const dayEvents = dayData.events || dayData.interviews || [];
      dayEvents.forEach((event) => {
        if (!event?.scheduledAt) return;
        if (getLocalDateKey(event.scheduledAt) === selectedDateKey) {
          matchedEvents.push(event);
        }
      });
    });

    return matchedEvents.sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setSelectedDate(null);
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setSelectedDate(null);
  };

  const handleDateClick = (date) => {
    if (!date) return;
    setSelectedDate(date);
  };

  const handleEventClick = (interview) => {
    console.log('Event clicked:', interview); // Debug log

    // Try different ways to get the applicationId
    let applicationId = null;

    if (interview.applicationId?._id) {
      applicationId = interview.applicationId._id;
    } else if (typeof interview.applicationId === 'string') {
      applicationId = interview.applicationId;
    } else if (interview.applicationId?.toString) {
      applicationId = interview.applicationId.toString();
    }

    console.log('Application ID:', applicationId); // Debug log

    if (applicationId) {
      navigate(`/applications/${applicationId}`);
    } else {
      console.error('No application ID found in interview object:', interview);
      alert('Unable to navigate: Application ID not found');
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'SCHEDULED':
        return 'bg-green-100 text-green-900';
      case 'COMPLETED':
        return 'bg-blue-100 text-blue-900';
      case 'CANCELLED':
        return 'bg-red-100 text-red-900';
      case 'NO_SHOW':
        return 'bg-yellow-100 text-yellow-900';
      default:
        return '';
    }
  };

  const getOutcomeClass = (outcome) => {
    switch (outcome) {
      case 'PASSED':
        return 'bg-green-600 text-white';
      case 'FAILED':
        return 'bg-red-600 text-white';
      default:
        return '';
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatEventTypeLabel = (eventType) => {
    // Use the official EVENT_TYPE_LABELS mapping
    return EVENT_TYPE_LABELS[eventType] || eventType?.charAt(0).toUpperCase() + eventType?.slice(1) || 'Event';
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const days = getDaysInMonth();
  const selectedInterviews = selectedDate ? getInterviewsForDate(selectedDate) : [];

  return (
    <MainLayout>
      <div className="p-6 max-w-[1400px] mx-auto">
        <div className="flex justify-between items-start mb-8 pb-4 border-b-2 border-gray-200">
          <div>
            <h1 className="m-0 mb-2 text-gray-800 text-3xl">Event Calendar</h1>
            <p className="m-0 text-gray-600 text-base">Schedule and manage interviews, exams, pre-departure, and departure</p>
          </div>
          {['HR', 'ADMIN'].includes(user?.role) && (
            <button
              className="px-5 py-2.5 bg-blue-600 text-white border-0 rounded-md text-base font-medium cursor-pointer transition-colors duration-200 hover:bg-blue-800"
              onClick={() => setShowScheduleModal(true)}
            >
              + Schedule Event
            </button>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          {/* Calendar Legend */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Event Types</h3>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-600 rounded"></div>
                <span className="text-sm text-gray-700">Interview</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-600 rounded"></div>
                <span className="text-sm text-gray-700">Exam</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-amber-500 rounded"></div>
                <span className="text-sm text-gray-700">Pre-Departure Orientation Training</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-purple-600 rounded"></div>
                <span className="text-sm text-gray-700">Departure</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-600 rounded"></div>
                <span className="text-sm text-gray-700">Completed</span>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center mb-6">
            <button onClick={handlePreviousMonth} className="px-4 py-2 bg-gray-600 text-white border-0 rounded-md cursor-pointer text-[0.95rem] transition-colors duration-200 hover:bg-gray-700">
              ← Previous
            </button>
            <h2 className="m-0 text-gray-800 text-2xl">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <button onClick={handleNextMonth} className="px-4 py-2 bg-gray-600 text-white border-0 rounded-md cursor-pointer text-[0.95rem] transition-colors duration-200 hover:bg-gray-700">
              Next →
            </button>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 px-5">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
              <p>Loading calendar...</p>
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-7 gap-2 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center font-semibold text-gray-700 p-3 bg-gray-50 rounded">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-2">
                {days.map((date, index) => {
                  const interviews = date ? getInterviewsForDate(date) : [];
                  const isSelected = selectedDate && date &&
                    date.toDateString() === selectedDate.toDateString();
                  const isToday = date &&
                    date.toDateString() === new Date().toDateString();

                  return (
                    <div
                      key={index}
                      className={`min-h-[100px] p-2 border rounded-md cursor-pointer transition-all duration-200 bg-white relative ${!date ? 'bg-gray-50 cursor-default border-transparent' : ''} ${isSelected ? 'border-blue-600 bg-blue-50 shadow-[0_2px_8px_rgba(0,123,255,0.3)]' : 'border-gray-300'} ${isToday ? 'border-green-600 bg-green-50' : ''} ${date && !isSelected && !isToday ? 'hover:border-blue-600 hover:shadow-[0_2px_8px_rgba(0,123,255,0.2)]' : ''}`}
                      onClick={() => handleDateClick(date)}
                    >
                      {date && (
                        <>
                          <span className="block font-semibold text-gray-800 mb-1">{date.getDate()}</span>
                          {interviews.length > 0 && (
                            <div className="space-y-1">
                              {interviews.slice(0, 2).map((interview, idx) => (
                                <div key={idx} className={`text-xs px-1.5 py-0.5 rounded text-center ${getEventTypeColor(interview.eventType || 'interview')}`}>
                                  {getEventTypeIcon(interview.eventType || 'interview')} {formatEventTypeLabel(interview.eventType)}
                                </div>
                              ))}
                              {interviews.length > 2 && (
                                <div className="text-xs text-gray-600 text-center">
                                  +{interviews.length - 2} more
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {selectedDate && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="m-0 mb-5 text-gray-800 text-xl">
              Events on {selectedDate.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </h3>
            {selectedInterviews.length === 0 ? (
              <p className="text-center text-gray-600 py-10 italic">No events scheduled for this day</p>
            ) : (
              <div className="flex flex-col gap-3">
                {selectedInterviews.map(interview => (
                  <div
                    key={interview.interviewId}
                    className={`flex items-center gap-4 p-4 bg-gray-50 rounded-md border-l-4 cursor-pointer transition-all duration-200 hover:bg-gray-100 hover:shadow-md ${getEventTypeClass(interview.eventType || 'interview')}`}
                    onClick={() => handleEventClick(interview)}
                  >
                    <div className="font-semibold text-gray-700 min-w-[80px] text-base">
                      {formatTime(interview.scheduledAt)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{getEventTypeIcon(interview.eventType || 'interview')}</span>
                        <div className="font-semibold text-gray-800">
                          {interview.title || `${formatEventTypeLabel(interview.eventType)} - ${interview.candidateName}`}
                        </div>
                      </div>
                      <div className="text-gray-600 text-sm mb-1">
                        {interview.candidateName} - {interview.jobPosting}
                      </div>
                      {interview.description && (
                        <div className="text-gray-600 text-sm italic">
                          {interview.description}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <span className={`px-3 py-1 rounded-xl text-xs font-semibold uppercase ${getEventTypeBadge(interview.eventType || 'interview')}`}>
                        {formatEventTypeLabel(interview.eventType)}
                      </span>
                      <span className={`px-3 py-1 rounded-xl text-xs font-semibold uppercase ${getStatusClass(interview.status)}`}>
                        {interview.status}
                      </span>
                      {interview.outcome !== 'PENDING' && (
                        <span className={`px-3 py-1 rounded-xl text-xs font-semibold uppercase ${getOutcomeClass(interview.outcome)}`}>
                          {interview.outcome}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showScheduleModal && (
        <ScheduleInterviewModal
          onClose={() => setShowScheduleModal(false)}
          onSuccess={() => {
            setShowScheduleModal(false);
            fetchCalendarData();
          }}
        />
      )}
    </MainLayout>
  );
};

export default InterviewCalendarPage;
